/* eslint-disable @typescript-eslint/no-explicit-any */
import { Tags } from "@/enum/tags";
import { TermRequest } from "@/pages/store-back-office/generate-term/data/types";
import { Paginated } from "src/@types/paginated";
import { GenerateTermRequest } from "../../@types/terms";
import { apiSlice } from "../http";

export enum TermSlaStatus {
  NOT_EXPIRED = "NOT_EXPIRED",
  EXPIRED = "EXPIRED",
  ALL = "ALL",
}

interface GetTermsParams {
  search: string;
  store_code: string;
  status: string;
  start_date: string;
  end_date: string;
  orderBy: Record<string, string>;
  page: number;
  perPage: number;
}

interface GetAllTermsParams {
  cpf?: string;
  user_name?: string;
  store_code?: string;
  reason?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  sla_status?: string;
  search: string;
  orderBy: string;
  perPage: number;
  meta: {
    page: number;
  };
}

interface TermHistory {
  id: string;
  created_at: string;
  type: string;
  status?: string;
  comment?: string;
  responsible: string;
  details?: string;
}

export const TERM_DOCUMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png";

export type TermDocumentRole = "BALANCE_EVIDENCE" | "GENERATED_TERM" | "SIGNED_TERM";

export interface TermDocument {
  id: string;
  file_name: string;
  file_type: string;
  created_at: string | null;
  term_request_role: TermDocumentRole;
}

export interface TermDocuments {
  balanceEvidence: TermDocument | null;
  generatedTerm: TermDocument | null;
  signedTerm: TermDocument | null;
}

export interface ReplaceSignedTermRequest {
  termId: string;
  file: File;
  comment: string;
  access_request_id?: string;
}

export const termRequestApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTerms: builder.query<Paginated<{ meta: any; data: TermRequest[] }>, GetTermsParams>({
      query: (filters) => {
        const params = new URLSearchParams();

        params.set("page", filters.page.toString());
        params.set("perPage", filters.perPage.toString());

        if (filters.search) params.set("search", filters.search);
        if (filters.store_code) params.set("store_code", filters.store_code);
        if (filters.status) params.set("status", filters.status);
        if (filters.start_date) params.set("start_date", filters.start_date);
        if (filters.end_date) params.set("end_date", filters.end_date);

        if (filters.orderBy && Object.keys(filters.orderBy).length > 0) {
          params.set("orderBy", JSON.stringify(filters.orderBy));
        } else {
          params.set("orderBy", "{}");
        }

        return {
          url: `/person/terms?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.TERMS],
      keepUnusedDataFor: 0,

      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const serialized = JSON.stringify({
          endpointName,
          page: queryArgs.page,
          perPage: queryArgs.perPage,
          search: queryArgs.search || "",
          store_code: queryArgs.store_code || "",
          status: queryArgs.status || "",
          start_date: queryArgs.start_date || "",
          end_date: queryArgs.end_date || "",
          orderBy: queryArgs.orderBy || {},
        });
        return serialized;
      },
    }),
    getAllTerms: builder.query<Paginated<{ meta: any; data: TermRequest[] }>, GetAllTermsParams>({
      query: ({ search, meta, orderBy, perPage, store_code, status, reason, sla_status, start_date, end_date }) => {

        const params = new URLSearchParams();

        params.append('search', search || '');
        params.append('orderBy', orderBy);
        params.append('page', meta.page.toString());
        params.append('perPage', perPage.toString());

        if (store_code) params.append('store_code', store_code);
        if (status) params.append('status', status);
        if (reason) params.append('reason', reason);
        if (sla_status) params.append('sla_status', sla_status);
        if (start_date) params.append('start_date', start_date);
        if (end_date) params.append('end_date', end_date);

        const url = `/person/terms/all?${params.toString()}`;

        return {
          url,
          method: "GET",
        };
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify({
          search: queryArgs.search,
          store_code: queryArgs.store_code,
          status: queryArgs.status,
          reason: queryArgs.reason,
          orderBy: queryArgs.orderBy,
          perPage: queryArgs.perPage,
        });
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!currentArg || !previousArg) return true;

        return currentArg.meta.page !== previousArg.meta.page ||
          JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      providesTags: [Tags.TERMS],
    }),
    getTermById: builder.query<TermRequest, string>({
      query: (id) => ({
        url: `/person/terms/${id}`,
        method: "GET",
      }),
      providesTags: [Tags.TERMS],
    }),
    getTermHistory: builder.query<TermHistory[], string>({
      query: (id) => ({
        url: `/person/terms/${id}/history`,
        method: "GET",
      }),
      providesTags: [Tags.TERMS],
    }),
    generateTerm: builder.mutation<{ pdfBase64: string; termId: string }, GenerateTermRequest>({
      query: ({ cpf, storeCode, data, file }) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(data));
        formData.append("file", file);

        return {
          url: `/person/${cpf}/${storeCode}/generate-pdf`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [Tags.TERMS],
    }),
    getTermPdf: builder.query<string, string>({
      query: (termId) => ({
        url: `/person/${termId}/generate-pdf`,
        method: "GET",
        responseHandler: "text",
      }),
      transformResponse: (response: string) => {
        try {
          return JSON.parse(response).pdfBase64;
        } catch {
          return response;
        }
      },
      providesTags: [Tags.TERMS],
    }),

    getTermDocuments: builder.query<TermDocuments, string>({
      query: (termId: string) => ({
        url: `/person/terms/${termId}/documents`,
        method: "GET",
      }),
      providesTags: [Tags.TERMS, Tags.FILE],
    }),
    uploadSignedTerm: builder.mutation<TermDocument, { termId: string; file: File }>({
      query: ({ termId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/person/terms/${termId}/signed-document`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [Tags.TERMS, Tags.FILE],
    }),
    replaceSignedTerm: builder.mutation<TermDocument, ReplaceSignedTermRequest>({
      query: ({ termId, file, comment, access_request_id }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("comment", comment);
        if (access_request_id) formData.append("access_request_id", access_request_id);
        return {
          url: `/person/terms/${termId}/signed-document/replacement`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [Tags.TERMS, Tags.FILE],
    }),
    updateTermStatus: builder.mutation<void, { termId: string; status: string; comment: string; access_request_id?: string }>({
      query: ({ termId, status, comment, access_request_id }) => ({
        url: `/person/terms/${termId}/status`,
        method: "PUT",
        body: { status, comment, access_request_id },
      }),
      invalidatesTags: [Tags.TERMS],
    }),
  }),
});

export const {
  useGetTermsQuery,
  useGetAllTermsQuery,
  useGetTermByIdQuery,
  useGetTermHistoryQuery,
  useGenerateTermMutation,
  useGetTermPdfQuery,
  useUpdateTermStatusMutation,
  useGetTermDocumentsQuery,
  useUploadSignedTermMutation,
  useReplaceSignedTermMutation,
} = termRequestApi;